import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { PermissionsService, AuthSyncResult } from "../../services/permissions/PermissionsService";
import { UserPermissions } from "../../model/members/UserPermissions";
import { Member } from '../../model/members/Member';
import { useClientOptional } from "../ClientProvider";
import { forceTokenRefresh, resetTokenRefreshCoordinator } from "../auth/tokenRefreshCoordinator";

interface MemberContextType {
    permissions: UserPermissions;
    refreshPermissions: () => Promise<void>;
    isLoading: boolean;
    currentMember: Member | null;
    isAdmin: boolean;
    needsValidation: boolean;
    // A member exists but is blocked (Suspended / Banned) — dead-end "contact the
    // club" state.
    isInactiveMember: boolean;
    // A member exists but is not yet active in a completion-eligible state
    // (Inactive / Pending / Cancelled) — they can complete/update their own
    // details to progress. Drives the blocking completion prompt on /my-profile.
    completionRequired: boolean;
    showValidationModal: boolean;
    setShowValidationModal: (show: boolean) => void;
    validateAuth: () => Promise<void>;
    validationErrorMessage: string | null;
}

const MemberContext = createContext<MemberContextType | undefined>(undefined);

const defaultPermissions: UserPermissions = {
    isLoading: true,
    member: {
        name: '',
        username: '',
        dateOfBirth: new Date(),
        emailAddress: '',
        addressLine1: '',
        addressLine2: '',
        townCity: '',
        countyState: '',
        postZipCode: '',
        phoneNumber: '',
        membershipId: '',
        specialOffers: false,
        status: 'Unknown',
        memberType: 'Member',
        isEnabled: false,
        validTo: new Date(),
        membershipCategory: {
            amount: 0,
            name: '',
            paymentFrequency: 'Unknown',
            isJointMembership: false,
            isVisible: false,
        },
        metadata: {},
        validFrom: new Date(),
        photoUrl: '',
        clientName: '',
        auth0Id: '',
        country: '',
    },
    authUser: {
        emailAddress: '',
        auth0Id: '',
        roles: [],
        permissions: [],
        clients: [],
    },
    permissions: [],
};

const TOKEN_REFRESH_FLAG_KEY = 'auth_sync_refreshed';

export const MemberProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { getAccessTokenSilently, isAuthenticated } = useAuth0();
    // Use the optional variant: MemberProvider sits inside ClientProvider, but ClientProvider
    // renders children as a bare fragment during the unauthenticated render pass, so the
    // Context.Provider may not yet be established. syncAuth() early-returns when unauthenticated
    // anyway, so a temporarily-undefined currentClientId is fine.
    const clientContext = useClientOptional();
    const currentClientId = clientContext?.currentClientId;
    const [permissions, setPermissions] = useState<UserPermissions>(defaultPermissions);
    const [isLoading, setIsLoading] = useState(true);
    const [needsValidation, setNeedsValidation] = useState(false);
    const [isInactiveMember, setIsInactiveMember] = useState(false);
    const [completionRequired, setCompletionRequired] = useState(false);
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [validationErrorMessage, setValidationErrorMessage] = useState<string | null>(null);
    const syncInFlightRef = useRef(false);

    // Clear the token-refresh guard on logout so the next session starts fresh
    useEffect(() => {
        if (!isAuthenticated) {
            sessionStorage.removeItem(TOKEN_REFRESH_FLAG_KEY);
            // Drop any in-flight coordinated refresh so a session boundary can't
            // hand the next user a promise from the previous session (FORT-293).
            resetTokenRefreshCoordinator();
        }
    }, [isAuthenticated]);

    const runSync = useCallback(async (): Promise<AuthSyncResult | null> => {
        if (!currentClientId) return null;

        const permissionsService = new PermissionsService(getAccessTokenSilently, currentClientId);
        return await permissionsService.syncAuth();
    }, [getAccessTokenSilently, currentClientId]);

    const syncAuth = useCallback(async () => {
        if (!isAuthenticated) {
            setPermissions(prev => ({ ...prev, isLoading: false }));
            setIsLoading(false);
            return;
        }

        // Prevent concurrent syncs (e.g. if refreshPermissions is called while a sync is already running)
        if (syncInFlightRef.current) return;
        syncInFlightRef.current = true;

        try {
            setIsLoading(true);

            let result = await runSync();
            if (!result) {
                // No client selected yet — clear state and let ClientProvider resolve clients first
                setPermissions({ ...defaultPermissions, isLoading: false });
                return;
            }

            // Handle the token refresh loop. The backend signals requiresTokenRefresh when it has
            // written either to the DB (new Auth0Id linked) or to Auth0 app_metadata (membership_id
            // added). The frontend forces a fresh token once, then re-syncs so the app state
            // reflects the new token claims. A session-storage guard prevents infinite loops if
            // Auth0's eventual consistency means the new metadata isn't visible on the refreshed token.
            if (result.requiresTokenRefresh) {
                const alreadyRefreshed = sessionStorage.getItem(TOKEN_REFRESH_FLAG_KEY) === 'true';
                if (alreadyRefreshed) {
                    console.warn(
                        '[MemberContext] /auth/sync requested a token refresh but this session ' +
                        'has already refreshed once. Skipping to avoid an infinite loop.'
                    );
                } else {
                    sessionStorage.setItem(TOKEN_REFRESH_FLAG_KEY, 'true');
                    // The token refresh may throw in test environments or if Auth0's refresh token
                    // is unavailable. Log and continue — the subsequent sync will use the existing
                    // token and still return the correct state.
                    try {
                        await forceTokenRefresh(getAccessTokenSilently);
                    } catch (refreshError) {
                        console.warn('[MemberContext] Token refresh failed, continuing with existing token',
                            refreshError);
                    }
                    // Re-run sync with the (hopefully) refreshed token so the MemberContext state
                    // reflects the new claims. This second call should be a no-op
                    // (requiresTokenRefresh: false) in a healthy system.
                    const refreshedResult = await runSync();
                    if (refreshedResult) result = refreshedResult;
                }
            }

            // Apply the result to React state
            switch (result.status) {
                case 'EmailMismatch':
                    setValidationErrorMessage(result.errorMessage ?? 'Email mismatch detected.');
                    setNeedsValidation(true);
                    setShowValidationModal(true);
                    setPermissions({ ...defaultPermissions, isLoading: false });
                    break;

                case 'Error':
                    setValidationErrorMessage(result.errorMessage ?? 'Failed to sync user.');
                    setPermissions({ ...defaultPermissions, isLoading: false });
                    break;

                case 'NewUser':
                case 'Success':
                default: {
                    const userPermissions = result.permissions;
                    setPermissions({
                        ...userPermissions,
                        permissions: userPermissions?.permissions ?? [],
                        isLoading: false,
                    });
                    setValidationErrorMessage(null);

                    // A member exists but is not active. Split by status:
                    // - Suspended / Banned → blocked "contact the club" dead-end.
                    // - Inactive / Pending / Cancelled → completion-eligible: the
                    //   member can complete/update their own details to progress.
                    const member = userPermissions?.member;
                    const memberExists = !!(member && member.membershipId);
                    const notActive = memberExists && !member.isEnabled;
                    const blocked = notActive && (member.status === "Suspended" || member.status === "Banned");
                    const completion =
                        notActive &&
                        (member.status === "Inactive" ||
                            member.status === "Pending" ||
                            member.status === "Cancelled");
                    setIsInactiveMember(blocked);
                    setCompletionRequired(completion);
                    if (blocked) {
                        // Only the blocked dead-end uses the "contact the club" modal.
                        // Completion-eligible members are handled by the blocking
                        // completion prompt on /my-profile (see App redirect).
                        setShowValidationModal(true);
                    } else {
                        setNeedsValidation(false);
                    }
                    break;
                }
            }
        } catch (error) {
            console.error('[MemberContext] Error during auth sync:', error);
            setPermissions({ ...defaultPermissions, isLoading: false });
            setValidationErrorMessage('There was an issue synchronising the user. Please try again.');
        } finally {
            syncInFlightRef.current = false;
            setIsLoading(false);
        }
    }, [isAuthenticated, runSync, getAccessTokenSilently]);

    // Run sync on auth state change
    useEffect(() => {
        syncAuth();
    }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

    // Public API compatibility — both names now point at the same sync flow
    const refreshPermissions = syncAuth;
    const validateAuth = syncAuth;

    const isAdmin = Array.isArray(permissions?.permissions) && permissions.permissions.some(p =>
        p.name === 'admin:access' ||
        p.name === 'admin:full' ||
        p.name === 'admin:fortius' ||
        p.name === 'admin:club' ||
        p.name === 'admin:club-owner' ||
        p.name === 'admin:frontofhouse'
    );

    return (
        <MemberContext.Provider value={{
            permissions,
            refreshPermissions,
            isLoading,
            currentMember: permissions.member,
            isAdmin,
            needsValidation,
            isInactiveMember,
            completionRequired,
            showValidationModal,
            setShowValidationModal,
            validateAuth,
            validationErrorMessage
        }}>
            {children}
        </MemberContext.Provider>
    );
};

export const useMember = () => {
    const context = useContext(MemberContext);
    if (context === undefined) {
        throw new Error('useMember must be used within a MemberProvider');
    }
    return context;
};

// Non-throwing variant for consumers that may render outside a MemberProvider
// (e.g. unit tests, or provider-tree positions where the member sync is merely
// informational). Returns undefined instead of throwing. Mirrors
// useClientOptional in ClientProvider.
export const useMemberOptional = (): MemberContextType | undefined => useContext(MemberContext);

export const usePermissions = useMember;
